import { useEffect, useState } from 'react'
import { z } from 'zod'

import type {
  ICreateTituloPremiado,
  ITituloPremiado,
} from '@/api/campaigns/titulo'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'

const tituloSchema = z.object({
  titulo: z.string().min(1, 'O título é obrigatório'),
  premio: z.string().min(1, 'O prêmio é obrigatório'),
})

interface Props {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: ICreateTituloPremiado) => void
  titulo: ITituloPremiado | null
}

export function TituloPremiadoModal({
  isOpen,
  onOpenChange,
  onSubmit,
  titulo,
}: Props) {
  const [formData, setFormData] = useState<ICreateTituloPremiado>({
    titulo: '',
    premio: '',
    campanha_id: 0,
    usuario_id: '',
  })

  const [errors, setErrors] = useState<{ titulo?: string; premio?: string }>({})

  useEffect(() => {
    if (titulo) {
      setFormData({
        titulo: titulo.titulo,
        premio: titulo.premio,
        campanha_id: titulo.campanha_id,
        usuario_id: titulo.usuario_id,
      })
    }
  }, [titulo])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const result = tituloSchema.safeParse({
      titulo: formData.titulo,
      premio: formData.premio,
    })

    if (!result.success) {
      const fieldErrors = result.error.flatten().fieldErrors
      setErrors({
        titulo: fieldErrors.titulo?.[0],
        premio: fieldErrors.premio?.[0],
      })
      return
    }

    setErrors({})
    onSubmit(formData)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {titulo ? 'Editar Título Premiado' : 'Adicionar Título Premiado'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Input
              name="titulo"
              placeholder="Nº do título"
              value={formData.titulo}
              onChange={handleChange}
            />
            {errors.titulo && (
              <p className="mt-1 text-sm text-red-500">{errors.titulo}</p>
            )}
          </div>
          <div>
            <Input
              name="premio"
              placeholder="Valor do prêmio"
              value={formData.premio}
              onChange={handleChange}
            />
            {errors.premio && (
              <p className="mt-1 text-sm text-red-500">{errors.premio}</p>
            )}
          </div>
          <Button type="submit" className="w-full">
            Salvar
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
